/**
 * FEAT-010 — expiry banner and "Expires" column for the OAuth Clients page.
 *
 * The lifecycle itself is computed server-side (GET /api/oauth/clients/list,
 * rules in @gxp-sentinel/shared/oauth/client-lifecycle); this module only turns
 * it into owner-facing notices. Only clients the tenant registered produce
 * notices: apps it merely authorized are their registrant's responsibility.
 */

import type { OAuthClientRow } from './api';

export type ExpiryNoticeTone = 'info' | 'warning' | 'critical';

export interface ExpiryNotice {
  readonly clientId: string;
  readonly tone: ExpiryNoticeTone;
  readonly message: string;
  readonly renewable: boolean;
}

const TONE_RANK: Readonly<Record<ExpiryNoticeTone, number>> = { critical: 0, warning: 1, info: 2 };

/** An expired-beyond-grace client is flagged for this long after grace ends, then only in the table. */
export const EXPIRED_NOTICE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function formatDate(value: string | null): string {
  if (!value) return 'an unknown date';
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? new Date(ms).toLocaleDateString() : 'an unknown date';
}

function recentlyExpired(client: OAuthClientRow, now: Date): boolean {
  const graceEnd = client.grace_ends_at ? Date.parse(client.grace_ends_at) : Number.NaN;
  return Number.isFinite(graceEnd) && now.getTime() - graceEnd < EXPIRED_NOTICE_WINDOW_MS;
}

function noticeFor(client: OAuthClientRow, now: Date): ExpiryNotice | null {
  if (!client.registered_by_you) return null;
  const name = `"${client.client_name}"`;

  switch (client.lifecycle_status) {
    case 'in_grace':
      return {
        clientId: client.client_id,
        tone: 'critical',
        renewable: client.renewable,
        message: `${name} expired on ${formatDate(client.expires_at)}. It keeps working during a grace period until ` +
          `${formatDate(client.grace_ends_at)} — renew it now or it will stop working.`,
      };
    case 'expired':
      if (!recentlyExpired(client, now)) return null;
      return {
        clientId: client.client_id,
        tone: 'warning',
        renewable: false,
        message: `${name} expired and can no longer be renewed. Register a new client and update your integration.`,
      };
    case 'expiring': {
      const days = client.expiry_warning_days;
      if (days === null) return null;
      return {
        clientId: client.client_id,
        tone: days === 1 ? 'critical' : days === 7 ? 'warning' : 'info',
        renewable: client.renewable,
        message: `${name} expires within ${days} day${days === 1 ? '' : 's'} (${formatDate(client.expires_at)}). ` +
          'Renew it to extend it by 90 days; any successful token grant also keeps an active client alive for 30 days.',
      };
    }
    default:
      return null;
  }
}

/** Owner-facing expiry notices, most urgent first. */
export function expiryNoticesFor(clients: readonly OAuthClientRow[], now: Date = new Date()): ExpiryNotice[] {
  return clients
    .map((client) => noticeFor(client, now))
    .filter((notice): notice is ExpiryNotice => notice !== null)
    .sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone]);
}

/** Text for the table's "Expires" column. */
export function expiryLabel(client: OAuthClientRow): string {
  switch (client.lifecycle_status) {
    case 'non_expiring':
      return 'Never';
    case 'in_grace':
      return `Expired — grace until ${formatDate(client.grace_ends_at)}`;
    case 'expired':
      return `Expired ${formatDate(client.expires_at)}`;
    default:
      return formatDate(client.expires_at);
  }
}
